import zod from 'zod';

import { ShowPathThumbnail } from '@sb/feathers-types/showPathThumbnail';
// Imported from the geometry-free `visionConfigSchemas` leaf rather than
// `LocateTypes`/`VisionInterface`, which transitively pull in
// `@sb/geometry`/`three`. `@sb/ai` is consumed by lightweight backend services
// (e.g. updater-bot) that do not bundle `three`. See visionConfigSchemas.ts.
import {
  CustomCheckpointModelType,
  Detection3DPoseEstimationConfig,
  DinoFastPrecision,
  DinoFastReferenceEmbedding,
  ProjectionMethodEnum,
  RegionOfInterest,
  ResultOrdering,
} from '@sb/routine-runner/vision/visionConfigSchemas';

// Model Architecture Model Types
export const MODEL_TYPE_VALUES = [
  'umi',
  'small',
  'rdt2',
  'rlt',
  'cosmos3',
] as const;
export type ModelType = (typeof MODEL_TYPE_VALUES)[number];

// ! WARNING When adding new data types, must also update RecorderEquipmentTypes in python/libs/training_data/get_training_data_task_hardware_config.py
// ! WARNING If you create a new bot hardware with this equipment type, then consider how this affects users that are using older echo-bot or skills-bot builds.
export const BotHardwareEquipmentType = zod.enum([
  'robot',
  'gripper',
  'camera',
  'force_sensor',
  'depth_sensor',
]);
export type BotHardwareEquipmentType = zod.infer<
  typeof BotHardwareEquipmentType
>;

// ! WARNING When adding new data types, must also update RecorderDataType in python/libs/training_data/get_training_data_task_hardware_config.py
// ! WARNING If you create a new bot hardware with this data type, then consider how this affects users that are using older echo-bot or skills-bot builds.
export const BotHardwareDataType = zod.enum([
  'pose',
  'joint_states',
  'joint_desires',
  'teleop_command',
  'gripper',
  'force_hardware',
  'video',
  'depth_image',
]);
export type BotHardwareDataType = zod.infer<typeof BotHardwareDataType>;

// Raw artifact this hardware delivers at recording time — the upload-facing
// counterpart of `dataType`. `dataType` says what KIND of stream the hardware
// records (video, pose, …); `rawArtifactType` says what FILE actually arrives
// for the skills pipeline (a `video` camera uploads an mp4 → raw_rgb_video,
// an Insta360 uploads a .insv container → insv). Values mirror the pipeline's
// hardware-recordable raw-input artifact vocabulary (api-srv stageRegistry
// RAW_INPUT_TYPES minus session_metadata, rosbag and raw_rgb_video_high_res,
// none of which any hardware config declares). Null for hardware that
// records no pipeline raw input (force_hardware).
export const RawArtifactType = zod.enum([
  'raw_rgb_video',
  'raw_depth_video',
  'pose',
  'gripper',
  'joint_states',
  'joint_desires',
  'teleop_command',
  'insv',
]);
export type RawArtifactType = zod.infer<typeof RawArtifactType>;

export const BotHardware = zod.object({
  id: zod.string(),
  name: zod.string(),
  equipmentType: BotHardwareEquipmentType,
  dataType: BotHardwareDataType,
  rawArtifactType: RawArtifactType.nullable().optional(),
  metadata: zod.any().nullable(),
  archived: zod.boolean().default(false),
  created: zod.date().optional(),
  customer: zod.string().optional(),
});
export type BotHardware = zod.infer<typeof BotHardware>;
// Pre-parse shape (defaults not yet applied — `archived` optional). The
// contract for callers that assemble hardware rows locally (e.g. the
// demonstration wizard's synthesized pose/gripper slots) before a zod parse
// applies the defaults.
export type BotHardwareInput = zod.input<typeof BotHardware>;

export const HardwareConfigSchema = zod.object({
  robots: zod.record(
    zod.string(),
    zod.object({
      isPrimary: zod.boolean(),
      hardware: zod.record(
        zod.string(),
        zod.object({
          id: zod.string(),
        }),
      ),
    }),
  ),
});
export type HardwareConfigSchema = zod.infer<typeof HardwareConfigSchema>;

/**
 * Hardware keys a training-data task declares as recovered by post-processing
 * rather than recorded by the rig — keyed by robot, mirroring the hardware
 * config's own nesting: `{ "robot_0": ["pose", "gripper"] }`.
 *
 * Name a recovered key after the channel it becomes: `pose` (recovered by
 * slam) and `gripper` (recovered by gripper_width_estimate). Those are the
 * names every existing Zarr dataset and every model architecture already uses,
 * so a key invented here — `pose_slam`, say — would export a channel nothing
 * reads.
 *
 * Empty by default, which means "the rig records everything" — the behaviour
 * of every task that has not been told otherwise.
 */
export const RecoveredHardwareKeysSchema = zod
  .record(zod.string(), zod.array(zod.string()))
  .default({});
export type RecoveredHardwareKeysSchema = zod.infer<
  typeof RecoveredHardwareKeysSchema
>;

/**
 * Read a declaration back out of the jsonb column, which hands over either the
 * already-parsed value or the string it was written as, depending on the query.
 *
 * Anything unreadable — unparseable, or parsing to something that is not a map
 * of robot key to key list — becomes an empty map: the task declares nothing,
 * which is what every task said before it could declare anything. Callers get
 * a usable map either way, so none of them has to re-check the shape.
 */
export const parseRecoveredHardwareKeys = (
  raw: unknown,
): RecoveredHardwareKeysSchema => {
  if (raw == null) return {};

  try {
    const value = typeof raw === 'string' ? JSON.parse(raw) : raw;
    const parsed = RecoveredHardwareKeysSchema.safeParse(value);

    return parsed.success ? parsed.data : {};
  } catch {
    return {};
  }
};

// Positioning-skill slot keys — shared with api-srv, the wizard, and echo-bot's recorder.
export const POSITIONING_SKILL_PRIMARY_ROBOT_KEY = 'primary';
export const POSITIONING_SKILL_POSE_HARDWARE_KEY = 'pose';
export const POSITIONING_SKILL_JOINT_STATES_HARDWARE_KEY = 'joint_states';
export const POSITIONING_SKILL_CAMERA_HARDWARE_KEYS = [
  'primary_camera',
  'secondary_camera',
  'tertiary_camera',
] as const;
export const POSITIONING_SKILL_MAX_CAMERAS =
  POSITIONING_SKILL_CAMERA_HARDWARE_KEYS.length;

// A hardware-key assignment: a bare equipment ID (legacy shorthand, still
// accepted so persisted routines keep parsing), or an object carrying an
// optional lens `feed` for dual-lens cameras (e.g. Insta360 front/back).
// Mirrored in `libs/feathers-types/skill.ts`'s
// `equipmentAssignmentValueSchema` (that lib can't import `@sb/ai/types` —
// keep the two unions textually aligned).
export const equipmentAssignmentValue = zod.union([
  zod.string(),
  zod.object({
    equipmentId: zod.string(),
    feed: zod.enum(['front', 'back']).optional(),
  }),
]);
export type EquipmentAssignmentValue = zod.infer<
  typeof equipmentAssignmentValue
>;

// The normalized object shape a hardware-key assignment always expands to
// (see `toEquipmentAssignment` in `libs/ai/helpers.ts`) — the object variant
// of `EquipmentAssignmentValue`, with the legacy bare-string shorthand
// already resolved to `equipmentId`.
export type EquipmentAssignment = {
  equipmentId: string;
  feed?: 'front' | 'back';
};

export const HardwareConfigEquipmentMapping = zod.record(
  zod.string(),
  zod.object({
    robotId: zod.string().optional(), // only required for non-primary robots
    robotEquipmentId: zod.string().optional(), // only required for non-primary robots
    hardwareKeyToEquipmentId: zod.record(
      zod.string(),
      equipmentAssignmentValue,
    ),
  }),
);
export type HardwareConfigEquipmentMapping = zod.infer<
  typeof HardwareConfigEquipmentMapping
>;

export const HydratedHardwareConfigSchema = zod.object({
  robots: zod.record(
    zod.string(),
    zod.object({
      isPrimary: zod.boolean(),
      hardware: zod.record(zod.string(), BotHardware),
    }),
  ),
});
export type HydratedHardwareConfigSchema = zod.infer<
  typeof HydratedHardwareConfigSchema
>;

const ParsedHardwareConfigEntry = zod.object({
  id: zod.string(),
  name: zod.string(),
  config: HardwareConfigSchema,
});

const ParsedPolicyConfigEntry = zod.object({
  id: zod.string(),
  name: zod.string(),
  config: zod.record(zod.string(), zod.any()),
});

const ParsedInferenceConfig = zod.object({
  obsRes: zod.array(zod.number()).optional(),
  obsHorizon: zod.number().optional(),
  frequency: zod.number().optional(),
  stepsPerInference: zod.number().optional(),
});

const ParsedModelArchitecture = zod.object({
  id: zod.string(),
  name: zod.string(),
  modelType: zod.string(),
  inputHardwareConfigId: zod.string(),
  outputHardwareConfigId: zod.string(),
  inputHardwareConfig: ParsedHardwareConfigEntry.optional(),
  outputHardwareConfig: ParsedHardwareConfigEntry.optional(),
  policyConfigId: zod.string().nullable().optional(),
  inferenceConfigId: zod.string().nullable().optional(),
  policyConfig: ParsedPolicyConfigEntry.nullable().optional(),
  inferenceConfig: ParsedInferenceConfig.nullable().optional(),
});

const ParsedModelMetadata = zod.object({
  id: zod.string(),
  created: zod.string(),
  finished: zod.string().nullable(),
  name: zod.string(),
  trainingDataProjectId: zod.string().nullable(),
  modelArchitecture: ParsedModelArchitecture,
  status: zod.string(),
  // Null when the download resolved to "newest saved checkpoint" and the
  // server could not confirm which epoch that was. The run's own epoch is
  // reported at the start of an epoch, so it is not a stand-in.
  epoch: zod.number().nullable(),
  dockerImageTag: zod.string().nullable(),
  hyperparameters: zod.record(zod.string(), zod.any()).nullable(),
  provider: zod.string().nullable(),
  neptuneUrl: zod.string().nullable(),
  logUrl: zod.string().nullable(),
  notes: zod.string().nullable(),
  gpuCount: zod.number().nullable(),
  gpuType: zod.string().nullable(),
  costPerHour: zod.number().nullable(),
  inferenceConfig: ParsedInferenceConfig.optional(),
});

export const ParsedModelMetadataPayload = zod.object({
  trainingRun: ParsedModelMetadata.optional(),
  botHardware: zod.record(zod.string(), BotHardware),
});

export const ParsedModel = zod.object({
  modelFilenames: zod.array(zod.string()),
  modelMetadata: ParsedModelMetadataPayload.optional(),
  modelDirectory: zod.string(),
});

export type ParsedModel = zod.infer<typeof ParsedModel>;

// Processor Settings
export const ZarrSettings = zod.object({
  enabled: zod.boolean(),
  timestampSync: zod.boolean(),
});
export type ZarrSettings = zod.infer<typeof ZarrSettings>;

export const McapSettings = zod.object({
  enabled: zod.boolean(),
  timestampSync: zod.boolean(),
  exportUrdf: zod.boolean().nullable().optional().default(true),
});
export type McapSettings = zod.infer<typeof McapSettings>;

export const SlamSettings = zod.object({
  enabled: zod.boolean().default(false),
  // YAMLs come back as null from the GraphQL response when unset on the
  // stored JSONB. echo-bot's auto-trigger doesn't propagate them through
  // settings (dpj reads from metadata.json directly), so the typical
  // stored shape is {enabled: true} with both YAMLs absent.
  calibrationYaml: zod.string().nullable().optional(),
  imuYaml: zod.string().nullable().optional(),
  // Opt-in two-pass cam-IMU hand-eye refinement (Park-Martin AX=XB).
  // No-op unless the session also has a paired GT recording linked via
  // `metadata.gt_session_id` and a seed `tFlangeCamYaml`.
  refineExtrinsics: zod.boolean().nullable().optional(),
});
export type SlamSettings = zod.infer<typeof SlamSettings>;

export const ProcessorSettings = zod.object({
  greenscreenProcessing: zod.boolean(),
  videoCompression: zod.boolean(),
  autoChunkOnMovement: zod.boolean().default(true),
  zarr: ZarrSettings,
  mcap: McapSettings,
  slam: SlamSettings.nullable().optional(),
});
export type ProcessorSettings = zod.infer<typeof ProcessorSettings>;

export const DEFAULT_PROCESSOR_SETTINGS: ProcessorSettings = {
  greenscreenProcessing: false,
  videoCompression: true,
  autoChunkOnMovement: true,
  zarr: {
    enabled: true,
    timestampSync: true,
  },
  mcap: {
    enabled: true,
    timestampSync: false,
    exportUrdf: true,
  },
  slam: {
    enabled: false,
  },
};

// Inference Config - used by skills-bot inference service
export const InferenceConfigSchema = zod
  .object({
    // Inference env config - set up in ConfigureInferenceRequest once per routine
    obs_res: zod.array(zod.number()).optional(),
    obs_horizon: zod.number(),
    robot_config: zod.record(zod.string(), zod.any()).optional(),
    // Runtime config - sent via StartInferenceRequest, can be overridden per-step
    steps_per_inference: zod.number(),
    frequency: zod.number(),
  })
  .passthrough(); // allow unknown fields to propagate
export type InferenceConfigSchema = zod.infer<typeof InferenceConfigSchema>;

// Policy Config - Hydra training config for diffusion/MLP policies
// This is a flexible schema since policy configs vary significantly between policy types
export const PolicyConfigSchema = zod
  .object({
    name: zod.string().optional(),
    _target_: zod.string(), // Workspace class target
    task_name: zod.string().optional(),
    shape_meta: zod.any().optional(),
    exp_name: zod.string().optional(),
    save_aws: zod.boolean().optional(),
    eval_test_set: zod.boolean().optional(),
    n_action_steps: zod.number().optional(),
    enabled_completion_prediction: zod.boolean().optional(),
    enabled_status_prediction: zod.boolean().optional(),
    enabled_goal_pose_prediction: zod.boolean().optional(),
    policy: zod.record(zod.string(), zod.any()), // Policy-specific config (varies by type)
    task: zod.record(zod.string(), zod.any()).optional(), // Task/dataset config (e.g. dataset.completion_distance_threshold)
    ema: zod.record(zod.string(), zod.any()).optional(),
    dataloader: zod.record(zod.string(), zod.any()).optional(),
    val_dataloader: zod.record(zod.string(), zod.any()).optional(),
    test_dataloader: zod.record(zod.string(), zod.any()).optional(),
    optimizer: zod.record(zod.string(), zod.any()).optional(),
    training: zod.record(zod.string(), zod.any()).optional(),
    logging: zod.record(zod.string(), zod.any()).optional(),
    checkpoint: zod.record(zod.string(), zod.any()).optional(),
    multi_run: zod.record(zod.string(), zod.any()).optional(),
    hydra: zod.record(zod.string(), zod.any()).optional(),
    defaults: zod.array(zod.any()).optional(),
  })
  .passthrough(); // allow unknown fields to propagate
export type PolicyConfigSchema = zod.infer<typeof PolicyConfigSchema>;

// ---------------------------------------------------------------------------
// Skill types — discriminated union keyed on `type`
// ---------------------------------------------------------------------------

export const SkillType = zod.enum([
  'control',
  'locate',
  'classify',
  'agent',
  'inspect',
  // Demonstration / show-path skills: skillType is the broad motion category;
  // the path-by-demonstration behavior is the `method` ('show-path').
  'ai-motion',
]);
export type SkillType = zod.infer<typeof SkillType>;

const BaseSkillSchema = zod.object({
  id: zod.string(),
  name: zod.string(),
  description: zod.string().nullable(),
  icon: zod.string().nullable().default(null),
  skillType: SkillType,
  method: zod.string(),
  hardwareConfigEquipmentMapping: HardwareConfigEquipmentMapping.default({}),
  createdAt: zod.string(),
  updatedAt: zod.string(),
});

// Control skill — fully defined

// Tracks which model checkpoint is active / downloaded on the robot
export const ControlModelVersion = zod.object({
  trainingRunId: zod.string(),
  // epoch / checkpointPath are null until the training run reports them; the
  // versions tab writes `run.epoch ?? null` when activating a ref.
  epoch: zod.number().int().nullable(),
  checkpointPath: zod.string().nullable(),
});
export type ControlModelVersion = zod.infer<typeof ControlModelVersion>;

export const ControlSkillMethod = zod.enum([
  'small_model',
  // 'foundation_model',
  // 'partner_model',
]);
export type ControlSkillMethod = zod.infer<typeof ControlSkillMethod>;

// TODO: decide if we want to merge into ParsedInferenceConfig (keeping separate for now)
export const ControlInferenceConfig = zod
  .object({
    frequency: zod.number().optional(),
    obsHorizon: zod.number().optional(),
    obsRes: zod.array(zod.number()).optional(),
    stepsPerInference: zod.number().optional(),
  })
  .passthrough();
export type ControlInferenceConfig = zod.infer<typeof ControlInferenceConfig>;

// Shared config for Control skills: Marvin links, checkpoint ref, inference
// knobs, camera setup. Per-method tuning lives in `methodConfig`.
export const SharedControlSkillConfig = zod.object({
  cameraCount: zod.union([zod.literal(1), zod.literal(2)]).default(2),
  taskId: zod.string().nullable().default(null),
  projectId: zod.string().nullable().default(null),
  activeModelRef: ControlModelVersion.nullable().default(null),
  modelVersions: zod.array(ControlModelVersion).default([]),
  inferenceConfig: ControlInferenceConfig.default({}),
  modelArchitectureId: zod.string().nullable().default(null),
});
export type SharedControlSkillConfig = zod.infer<
  typeof SharedControlSkillConfig
>;

export const ControlSkill = BaseSkillSchema.extend({
  skillType: zod.literal('control'),
  method: ControlSkillMethod,
  shared: SharedControlSkillConfig,
  // Method-specific runtime config (e.g. diffusion steps, noise schedule for
  // small_model). Shape TBD — using a passthrough record until we know what
  // each method needs.
  methodConfig: zod.record(zod.string(), zod.unknown()).default({}),
});
export type ControlSkill = zod.infer<typeof ControlSkill>;

// Locate skill

// Shared envelope for every Locate skill — pose, camera, and projection.
// Per-method tuning knobs (positives/negatives, thresholds, GPU runner) live in
// the method-specific config below.

export const PoseSchema = zod.object({
  kind: zod.enum(['tooltip', 'joint']).default('tooltip'),
  x: zod.number(),
  y: zod.number(),
  z: zod.number(),
  roll: zod.number(),
  pitch: zod.number(),
  yaw: zod.number(),
  // Real arm joint angles (radians, J0..J5) captured alongside the Cartesian
  // pose. Optional because vision-derived poses (plane corners, detections)
  // have no joint solution; a captured robot pose persists them so the saved
  // skill can return to the exact taught arm configuration rather than an IK
  // re-solve of the tooltip pose. The 6-tuple is spelled out inline (rather
  // than via `@sb/utilities`' `six`) so `ai/types` imports nothing from the
  // `@sb/utilities` barrel — that barrel eagerly pulls in `eventemitter3` via
  // `EventEmitter`, which lightweight bundled consumers (e.g. updater-bot) do
  // not ship. Structurally matches `ArmJointPositions`, so consumers need no
  // cast.
  jointAngles: zod
    .tuple([
      zod.number(),
      zod.number(),
      zod.number(),
      zod.number(),
      zod.number(),
      zod.number(),
    ])
    .optional(),
});
export type PoseSchema = zod.infer<typeof PoseSchema>;

export const ProjectionPlane = zod.object({
  origin: PoseSchema,
  xAxis: PoseSchema,
  yAxis: PoseSchema,
});
export type ProjectionPlane = zod.infer<typeof ProjectionPlane>;

// Fit parameters for the `DetectedPlane` method. Still read (and left saved)
// under `DetectedDepth`, which fits nothing: switching the operator's "Detect
// surface tilt" switch back on restores their own settings rather than a
// default.
export const DepthSurfaceConfig = zod.object({
  // How closely a match's depth must agree with the surface fitted to it before
  // that surface is accepted. The preset expands into the underlying RANSAC fit
  // parameters (inlier tolerance, max tilt, inlier floor) at the runtime
  // boundary — `DEPTH_SURFACE_STRICTNESS_PRESETS` in routine-runner's
  // `mapSkillToLocate` holds the numbers. One operator-facing knob rather than
  // three: the individual parameters are coupled, and only these combinations
  // differ in behaviour an operator can act on.
  strictness: zod
    .enum(['permissive', 'balanced', 'strict'])
    .default('balanced'),
  // What happens to a match whose surface the fit rejected: grasp it top-down
  // from the world frame (on) or leave it out of the results (off). Independent
  // of `strictness`, which decides only what counts as a good surface — how
  // strict the fit is and whether an unmeasurable match is still worth picking
  // are separate calls, and tightening the fit shouldn't silently drop matches.
  // Defaults to on, matching the behaviour of the permissive/balanced presets
  // that previously carried this flag.
  fallbackToTopDown: zod.boolean().default(true),
});
export type DepthSurfaceConfig = zod.infer<typeof DepthSurfaceConfig>;

/**
 * A persisted numeric that survives the `null` a cleared UI number input leaves
 * behind. `NumberInput` emits `NaN` for an empty field, and `JSON.stringify(NaN)`
 * is `null` — which a plain `zod.number().default(x)` does NOT repair, because
 * `.default()` substitutes only for `undefined`.
 *
 * That distinction is load-bearing here: a single `null` fails the whole
 * `LocateSkill` parse, and `getLocateSkillById` reports an unparseable skill as
 * a *missing* one, so one cleared field makes a routine unrunnable and blames a
 * skill that is still sitting right there. Coercing on read also repairs rows
 * already written that way, without a migration.
 *
 * Only `null` and non-finite numbers are repaired — any other wrong type still
 * fails validation loudly rather than being silently zeroed.
 */
function clearableNumber(fallback: number) {
  return zod.preprocess(
    (value) =>
      value === null || (typeof value === 'number' && !Number.isFinite(value))
        ? fallback
        : value,
    zod.number().default(fallback),
  );
}

export const ProjectionConfig = zod.object({
  // How 2D detections become 3D poses — one discriminator, named for what each
  // method measures. See `ProjectionMethod` in routine-runner's Locate
  // Arguments, which this maps onto 1:1 and which documents the four values.
  method: ProjectionMethodEnum.default('ManualPlane'),
  // ManualPlane fields (ignored by the other three methods). A freshly-taught
  // plane is stored inline (origin + X-axis + Y-axis poses); mutually exclusive
  // with planeSpaceItemId. `null` when not yet configured.
  plane: ProjectionPlane.nullable().default(null),
  // Set instead of `plane` when linking an existing global Space plane by ID.
  planeSpaceItemId: zod.string().nullable().default(null),
  // Object height above the reference plane, in mm (ManualPlane only). At
  // detection time the projection plane is offset along its normal by this
  // much before ray-casting, so match poses land on the object's top surface
  // instead of the plane. Absent/null on older skills = 0.
  objectHeight: zod.number().nullable().optional(),
  // Detection3D config (null unless method === 'Detection3D'). Reuses
  // the shared `Detection3DPoseEstimationConfig` (defined in visionConfigSchemas;
  // also extended by the routine Locate step's Detection3DParams).
  detection3d: Detection3DPoseEstimationConfig.nullable().default(null),
  // Surface-fit parameters, read by DetectedPlane and kept saved under
  // DetectedDepth. Null on the two methods that fit nothing at all.
  depthSurface: DepthSurfaceConfig.nullable().default(null),
  transform: zod
    .object({
      // Grasp frame: defines the base in-plane orientation AND the frame the
      // X/Y/Z + roll/pitch/yaw offset rides in. 'object' = detection principal
      // axis (default), 'plane' = plane X, 'world' = world X on the plane.
      // On the plane-less methods 'plane' is not offered; for DetectedPlane the
      // in-plane datum is the stereographic X axis of the fitted surface.
      graspFrame: zod.enum(['object', 'plane', 'world']).default('object'),
      // `clearableNumber` rather than `zod.number().default(0)`: all six are
      // editable as number inputs, so a cleared field can reach the DB as `null`.
      x: clearableNumber(0), // mm
      y: clearableNumber(0), // mm
      z: clearableNumber(0), // mm
      roll: clearableNumber(0), // deg
      pitch: clearableNumber(0), // deg
      yaw: clearableNumber(0), // deg
    })
    .nullable()
    .default(null),
});
export type ProjectionConfig = zod.infer<typeof ProjectionConfig>;

export const MatchesConfig = zod.object({
  limitResults: zod.number().int().min(1).max(100).default(50),
  orderBy: ResultOrdering.default('top-left-to-bottom-right'),
  // When true (default), each match is refined with a segmentation pass for a
  // pixel-accurate mask + tighter box; when false, the heatmap-patch bounding
  // box is used directly. Maps to DinoFastParams.enableSegmentationMask.
  enableSegmentationMask: zod.boolean().default(true),
  // Which segmentation backend refines OBBs into masks when
  // enableSegmentationMask is on. Maps to DinoFastParams.segmentationMethod,
  // whose comment carries the reasoning behind the 'bilateral_solver' default.
  // Legacy rows may carry the removed 'guided_filter'; coerce it to
  // 'bilateral_solver' so they keep parsing (matches DinoFastParams).
  segmentationMethod: zod
    .preprocess(
      (v) => (v === 'guided_filter' ? 'bilateral_solver' : v),
      zod.enum(['sam2', 'bilateral_solver', 'heatmap_threshold']),
    )
    .default('bilateral_solver'),
});
export type MatchesConfig = zod.infer<typeof MatchesConfig>;

export const SharedLocateSkillConfig = zod.object({
  // A freshly-captured training pose is stored inline; selecting an existing
  // global Space position links it via `capturePoseSpaceItemId` instead.
  // Exactly one is set once the Camera & Capture Pose step is complete.
  capturePose: PoseSchema.nullable().default(null),
  capturePoseSpaceItemId: zod.string().nullable().default(null),
  cameraId: zod.string(),
  regionOfInterest: RegionOfInterest.optional(),
  matches: MatchesConfig.default({
    limitResults: 50,
    orderBy: 'top-left-to-bottom-right',
    enableSegmentationMask: true,
    segmentationMethod: 'bilateral_solver',
  }),
  // Every field inside `ProjectionConfig` has a default, but the object itself
  // had none — so a skill persisted before its Projection step was completed
  // failed the parse outright and reported as *missing*. Defaulting it lets the
  // skill load and be flagged as needing a reference plane, which is a fixable
  // message instead of a false "not found".
  projection: ProjectionConfig.default({}),
  // Name of the Space position list that locate results write to at runtime.
  // Drives the routine-runner Locate step's `positionListName` argument.
  positionListName: zod.string().default('Locate results'),
  /**
   * Storage id of the taught snapshot — a JPEG of the camera frame with the
   * detection overlays baked in, captured when teaching is confirmed. Shown on
   * the skill detail General tab. Absent on skills taught before this existed
   * or whose teach produced no result; the General tab then shows an empty
   * state. Not read by the locate runtime.
   */
  snapshotStorageID: zod.string().nullable().optional(),
  /**
   * How many detections that snapshot shows. A save whose frame found nothing
   * keeps a snapshot that found something. Absent on snapshots stored before
   * the count was recorded.
   */
  snapshotMatchCount: zod.number().int().nonnegative().optional(),
  orientationReference: zod
    .object({
      // Normalized reference contour: array of [x, y] points.
      referenceContour: zod.array(zod.array(zod.number()).length(2)),
    })
    .nullable()
    .default(null),
  /**
   * Why teach produced no `orientationReference`. Null when a reference
   * exists, or when none was ever attempted.
   *
   * Persisted because the Projection step cannot recover the reason from a
   * null `orientationReference`, and the three cases differ in what the
   * operator should do — and in one case, in whether anything is wrong at all:
   * a `symmetric` part can never carry an object frame; a `sceneInconsistent`
   * one usually just needs better lighting or a tighter ROI before
   * re-teaching; a `pcaSufficient` one is fine as it stands, because the part
   * is elongated enough for each detection's own outline to resolve the frame,
   * which is all a reference could have offered for a 180-degree-symmetric
   * shape. Only that last case leaves object-frame grasping available. Not
   * read by the locate runtime.
   */
  orientationReferenceRejection: zod
    .enum(['symmetric', 'sceneInconsistent', 'pcaSufficient'])
    .nullable()
    .default(null),
});
export type SharedLocateSkillConfig = zod.infer<typeof SharedLocateSkillConfig>;

// Serialized weights for the learned locate scorer. Mirror of routine-runner's
// `DinoFastLocateHead`, trained server-side by vision-bot. When present this
// replaces the cosine scorer; when null, locate falls back to cosine. `kind`
// selects the model: 'mlp' (single-hidden-layer net) or 'logistic' (linear).
const DinoFastMlpHeadWeights = zod.object({
  kind: zod.literal('mlp'),
  version: zod.literal(1),
  inputDim: zod.number().int(),
  hidden: zod.number().int(),
  w1: zod.array(zod.number()), // hidden * inputDim, row-major [h][i]
  b1: zod.array(zod.number()), // hidden
  w2: zod.array(zod.number()), // hidden
  b2: zod.number(),
});

const DinoFastLogisticHeadWeights = zod.object({
  kind: zod.literal('logistic'),
  version: zod.literal(1),
  inputDim: zod.number().int(),
  w: zod.array(zod.number()), // inputDim
  b: zod.number(),
});

export const DinoFastLocateHead = zod.discriminatedUnion('kind', [
  DinoFastMlpHeadWeights,
  DinoFastLogisticHeadWeights,
]);

export type DinoFastLocateHead = zod.infer<typeof DinoFastLocateHead>;

/**
 * One committed ClickFind training take: the frame the operator annotated and
 * the points they placed on it.
 *
 * Persisted, so a reload still shows the gallery and the coordinates. The frame
 * is a storage id rather than bytes — fetched as `/storage/<id>`, the same
 * mechanism the taught snapshot uses — and feathers-bot's mark-and-sweep
 * reclaims blobs no record references, so dropping a take here is all that is
 * needed to release its image.
 *
 * Distinct from `positives`/`negatives`: those are the embeddings the runtime
 * scores against, with no notion of which frame or which click produced them.
 * These are the operator's record of what they taught and where.
 */
export const ClickFindTrainingTake = zod.object({
  id: zod.string(),
  /** Storage id of the composited frame, markers baked in. */
  imageStorageID: zod.string(),
  clicks: zod
    .array(
      zod.object({
        // Normalized original-image coordinates in [0, 1], stored
        // grid-independently like the session clicks they came from, so a
        // precision change doesn't move them.
        nx: zod.number(),
        ny: zod.number(),
        kind: zod.enum(['positive', 'negative']),
      }),
    )
    .default([]),
  capturedAt: zod.number(),
});
export type ClickFindTrainingTake = zod.infer<typeof ClickFindTrainingTake>;

export const DinoFastSkillConfig = zod.object({
  gpuTaskRunnerId: zod.string().default(''),
  embeddingModelVersion: zod.string().default('dinov3-vits16-v1'),
  embeddingDim: zod.number().int().default(384),
  // Mirrors DinoFastParams: the CPU tier, since gpuTaskRunnerId defaults to ''.
  precision: DinoFastPrecision.default('standard'),
  positives: zod.array(DinoFastReferenceEmbedding).default([]),
  negatives: zod.array(DinoFastReferenceEmbedding).default([]),
  // Learned scorer trained in the teach UI. Null on legacy/untaught steps,
  // which fall back to cosine scoring at runtime.
  locateHead: DinoFastLocateHead.nullable().default(null),
  scoreThreshold: zod.number().min(-2).max(2).default(0.5),
  // Match-area bounds in cm², on the surface each match is projected onto.
  // Only a ManualPlane or depth projection can measure that, so a skill with
  // neither keeps every match. A max at/above LOCATE_MAX_MATCH_AREA_CM2 means
  // "no upper limit"; absent bounds are open.
  minMatchAreaCm2: zod.number().min(0).optional(),
  maxMatchAreaCm2: zod.number().min(0).optional(),
  nmsIouThreshold: zod.number().min(0).max(1).default(0.3),
  maxBlobs: zod.number().int().min(1).default(20),
  // Every take the operator committed, oldest first. Defaults to empty, so
  // skills taught before this existed simply show an empty gallery.
  trainingTakes: zod.array(ClickFindTrainingTake).default([]),
});
export type DinoFastSkillConfig = zod.infer<typeof DinoFastSkillConfig>;

// Mirrors the runner's `Blob2DParams`
// (`libs/routine-runner/vision/LocateTypes.ts`) field-for-field, with identical
// defaults. Keep the two in sync; a divergence would surface as a parse error
// at locate time.
export const BlobSkillConfig = zod.object({
  minArea: zod.number().default(1000),
  maxArea: zod.number().default(10000),
  minThreshold: zod.number().default(50),
  maxThreshold: zod.number().default(220),
  minCircularity: zod.number().default(0.1),
  maxCircularity: zod.number().default(1),
  filterByCircularity: zod.boolean().default(false),
  invertImage: zod.boolean().default(false),
  disableRotation: zod.boolean().default(false),
});
export type BlobSkillConfig = zod.infer<typeof BlobSkillConfig>;

// Mirrors the runner's `Shape2DParams` + `TemplateImage`
// (`libs/routine-runner/vision/LocateTypes.ts`) field-for-field, with identical
// defaults. This is a FLAT config: `templateImage` is a peer key alongside the
// Shape2DParams fields — exactly the shape `mapSkillMethodToLocateMethod`'s
// `shape_match` branch reads. Keep the two in sync; a divergence would surface
// as a parse error at locate time.
export const ShapeMatchSkillConfig = zod.object({
  minScale: zod.number().default(0.8),
  maxScale: zod.number().default(1.2),
  maxRotation: zod.number().default(180),
  matchThreshold: zod.number().default(0.5),
  boundingBox: zod
    .object({
      rotationDegrees: zod.number().default(0),
      scaleX: zod.number().default(1),
      scaleY: zod.number().default(1),
    })
    .default({}),
  filterIntersections: zod.boolean().default(true),
  disableRotation: zod.boolean().default(false),
  rejectOnEdge: zod.boolean().default(false),
  // Peer key (mirrors `TemplateImage`), not nested under a `settings` object.
  templateImage: zod.object({ storageID: zod.string() }).default({
    storageID: '',
  }),
  /**
   * The mask the template was cut out of, kept so re-teaching reopens the
   * drawing instead of a blank veil. The template alone cannot stand in for it:
   * it is cropped to the mask's bounds, so where it sat in the frame is lost.
   *
   * Never read at locate time — the detector matches `templateImage`. Empty for
   * skills whose template predates this, which simply start their next edit from
   * a clean mask.
   */
  templateMask: zod.object({ storageID: zod.string() }).default({
    storageID: '',
  }),
});
export type ShapeMatchSkillConfig = zod.infer<typeof ShapeMatchSkillConfig>;

// Mirrors the runner's `TextPromptDetection2DParams`
// (`libs/routine-runner/vision/LocateTypes.ts`) field-for-field. Keep the two
// in sync; the only intentional divergence is the `.min(0).max(1)` bound on
// `confidenceThreshold` here.
export const TextPromptSkillConfig = zod.object({
  gpuTaskRunnerId: zod.string().default(''),
  textInput: zod.string().default(''),
  useSam: zod.boolean().default(false),
  confidenceThreshold: zod.number().min(0).max(1).default(0.35),
  // Match-area bounds in cm²; see DinoFastSkillConfig.
  minMatchAreaCm2: zod.number().min(0).optional(),
  maxMatchAreaCm2: zod.number().min(0).optional(),
});
export type TextPromptSkillConfig = zod.infer<typeof TextPromptSkillConfig>;

// Mirrors the runner's `CustomCheckpointDetection2DParams`
// (`libs/routine-runner/vision/LocateTypes.ts`) field-for-field. The runtime
// params require `gpuTaskRunnerId`/`modelPath` (no default); this skill config
// defaults them to '' so the wizard can start empty and gate Continue via
// `validateCustomCheckpointTeach`. `mapSkillMethodToLocateMethod` re-parses
// against the strict runtime schema at locate time. Keep the two in sync.
export const CustomCheckpointSkillConfig = zod.object({
  gpuTaskRunnerId: zod.string().default(''),
  modelPath: zod.string().default(''),
  modelType: CustomCheckpointModelType.default('YoloV11'),
  thresholdingParams: zod.record(zod.string(), zod.string()).default({
    confidence_threshold: '0.3',
    nms_threshold: '0.4',
    max_detections: '100',
  }),
  // Model source: 'checkpoint' (a runner-hosted checkpoint file, the default)
  // or 'roboflow' (a Roboflow-hosted model). The roboflow* fields are
  // UI + persistence only — the locator does not read them yet (see
  // docs/plans/2026-07-14-roboflow-locate-model-source-design.md).
  modelSource: zod.enum(['checkpoint', 'roboflow']).default('checkpoint'),
  // Model url "<workspace>/<project>/<version>".
  roboflowDisplayName: zod.string().optional(),
  roboflowProjectSlug: zod.string().optional(),
  roboflowWorkspaceName: zod.string().optional(),
  // Workflow selection. A workflow is a graph that can publish several outputs,
  // so a complete selection names the output locate reads as well as the
  // workflow itself.
  roboflowWorkflowId: zod.string().optional(),
  roboflowOutputName: zod.string().optional(),
  // Inputs bound at run time to switch the graph's own filtering off, so the
  // skill's thresholds are the only ones that apply. Only the workflow's schema
  // says which it accepts, so they are resolved when it is imported.
  roboflowWorkflowInputs: zod.record(zod.string(), zod.string()).optional(),
});
export type CustomCheckpointSkillConfig = zod.infer<
  typeof CustomCheckpointSkillConfig
>;

export const LocateSkillMethod = zod.enum([
  'dino_fast', // UI label: "ClickFind AI"
  'blob',
  'shape_match',
  'text_prompt',
  'custom_checkpoint',
  'mock',
]);
export type LocateSkillMethod = zod.infer<typeof LocateSkillMethod>;

export const LocateSkill = BaseSkillSchema.extend({
  skillType: zod.literal('locate'),
  method: LocateSkillMethod,
  shared: SharedLocateSkillConfig,
  methodConfig: zod.record(zod.string(), zod.unknown()).default({}),
}).partial({
  description: true,
  createdAt: true,
  updatedAt: true,
});
export type LocateSkill = zod.infer<typeof LocateSkill>;

// Classify skill — stub (config schemas deferred)

export const ClassifySkillMethod = zod.enum(['template_match']);
export type ClassifySkillMethod = zod.infer<typeof ClassifySkillMethod>;

// Placeholder until the classify wizard work begins.
export const SharedClassifySkillConfig = zod.object({}).passthrough();
export type SharedClassifySkillConfig = zod.infer<
  typeof SharedClassifySkillConfig
>;

export const ClassifySkill = BaseSkillSchema.extend({
  skillType: zod.literal('classify'),
  method: ClassifySkillMethod,
  shared: SharedClassifySkillConfig,
  methodConfig: zod.record(zod.string(), zod.unknown()).default({}),
});
export type ClassifySkill = zod.infer<typeof ClassifySkill>;

// Agent skill — free-form instruction text for domain context

export const AgentSkillMethod = zod.enum(['instructions']);
export type AgentSkillMethod = zod.infer<typeof AgentSkillMethod>;

export const SharedAgentSkillConfig = zod.object({
  instructions: zod.string().default(''),
});
export type SharedAgentSkillConfig = zod.infer<typeof SharedAgentSkillConfig>;

export const AgentSkill = BaseSkillSchema.extend({
  skillType: zod.literal('agent'),
  method: AgentSkillMethod,
  shared: SharedAgentSkillConfig,
  methodConfig: zod.record(zod.string(), zod.unknown()).default({}),
});
export type AgentSkill = zod.infer<typeof AgentSkill>;

// Show-path (demonstration playback) skill

export const ShowPathSkillMethod = zod.enum(['show-path']);
export type ShowPathSkillMethod = zod.infer<typeof ShowPathSkillMethod>;

/**
 * What the recorded path is anchored to. `robot-space` means not anchored —
 * the path replays in the recorded world frame. The wizard writes this from
 * the moment a draft exists, so read `kind` rather than treating a present
 * `anchor` as a configured one.
 *
 * Only `kind` is required: a row persisted before one of the target fields
 * existed must still finalize.
 */
export const DemonstrationAnchor = zod.discriminatedUnion('kind', [
  zod.object({ kind: zod.literal('robot-space') }),
  zod.object({
    kind: zod.literal('locate-skill'),
    locateSkillId: zod.string().nullish(),
  }),
  zod.object({
    kind: zod.literal('pose'),
    positionListID: zod.string().nullish(),
    positionListIndex: zod.number().int().optional(),
    pose: PoseSchema.nullish(),
  }),
]);
export type DemonstrationAnchor = zod.infer<typeof DemonstrationAnchor>;

/**
 * True when a non-robot-space anchor names nothing to anchor to.
 *
 * Picking a kind mints its empty shape immediately and the wizard's unmount
 * autosave persists it, so a finalized skill can carry a kind with no target.
 * A `pose` anchor is targetless only when it has neither a linked Space item
 * nor an inline taught pose -- an inline pose has no `positionListID` and is
 * still a reference.
 */
export function anchorHasNoTarget(anchor: DemonstrationAnchor): boolean {
  switch (anchor.kind) {
    case 'locate-skill':
      return !anchor.locateSkillId;
    case 'pose':
      return !anchor.positionListID && !anchor.pose;
    case 'robot-space':
      return false;
    default:
      // A new kind reaches here rather than being reported as configured.
      throw new Error(
        `anchorHasNoTarget: unhandled anchor kind ${
          (anchor as { kind: string }).kind
        }`,
      );
  }
}

/**
 * How the operator drove the arm while recording. Spelled differently from
 * `@sb/sb-robot-api`'s ephemeral-session modality (`iosTeleop`).
 */
export const DemonstrationModality = zod.enum([
  'antigravity',
  'haptic',
  'ios-teleop',
]);
export type DemonstrationModality = zod.infer<typeof DemonstrationModality>;

/**
 * Everything the show-path wizard persists under `shared`. Lives beside the
 * `ShowPathSkill` variant so the variant validates its own config rather than
 * carrying a passthrough envelope; a field the wizard writes but this omits
 * would be stripped by a parse. Nearly all optional — the wizard writes
 * `shared` in stages.
 *
 * Split from the refined schema below so `DraftSkillInput` has a plain object
 * to `.partial()`.
 */
const SharedShowPathSkillConfigBase = zod.object({
  // Ids of the takes recorded for this skill, newest last. Functional default:
  // a literal `[]` would be shared by every parse.
  recordings: zod.array(zod.string()).default(() => []),
  // Locate skill the RunDemonstration step view defaults its dropdown to.
  associatedLocateSkillId: zod.string().nullable().optional(),
  // What the path is anchored to. Left undefaulted: `{ kind: 'robot-space' }`
  // is a written value, so a default would fake one on rows that never had it.
  anchor: DemonstrationAnchor.optional(),
  // How the take was driven, and in which tooltip ('wrist' or an ee-* id).
  modality: DemonstrationModality.optional(),
  activeTooltipOption: zod.string().optional(),
  // Pose the arm moves to before recording. Not the path's reference frame.
  startingPoseItemId: zod.string().nullable().optional(),
  startingPose: PoseSchema.nullable().optional(),
  // Playback settings, in the recording's frame-timestamp domain (seconds).
  // Null speed plays at 1.0; null trim bounds replay the full recording.
  speed: zod.number().min(0.1).max(2).nullable().default(null),
  trimStartTimestamp: zod.number().finite().nullable().default(null),
  trimEndTimestamp: zod.number().finite().nullable().default(null),
  // Written by the server from the newest recording and the trim window.
  pathThumbnail: ShowPathThumbnail.optional(),
});

/**
 * A window with `start > end` selects no frames at all, which the replay path
 * would read as an empty trajectory rather than an error. Reject it at the
 * write boundary instead. Equal bounds stay legal — a single-frame window is
 * degenerate but well-defined.
 */
function checkTrimWindowOrdering(
  data: Partial<zod.infer<typeof SharedShowPathSkillConfigBase>>,
  ctx: zod.RefinementCtx,
): void {
  const { trimStartTimestamp: start, trimEndTimestamp: end } = data;

  if (start == null || end == null) return;

  if (start > end) {
    // Both bounds carry the issue, so a consumer scoping failures to the
    // fields it wrote sees it whichever bound moved.
    for (const path of ['trimStartTimestamp', 'trimEndTimestamp']) {
      ctx.addIssue({
        code: zod.ZodIssueCode.custom,
        path: [path],
        message:
          'trimStartTimestamp must be less than or equal to trimEndTimestamp',
      });
    }
  }
}

export const SharedShowPathSkillConfig =
  SharedShowPathSkillConfigBase.superRefine(checkTrimWindowOrdering);
export type SharedShowPathSkillConfig = zod.infer<
  typeof SharedShowPathSkillConfig
>;

/**
 * The name this config is known by outside the skill-variant union — what
 * `parseDemonstrationSkill` returns, and what a show-path skill contract folds
 * in.
 */
export const DemonstrationSkillConfig = SharedShowPathSkillConfig;
export type DemonstrationSkillConfig = SharedShowPathSkillConfig;

/**
 * Read a show-path skill row's config, rejecting a row of another skill type.
 *
 * Takes the row structurally rather than as `@sb/feathers-types`' `SkillData`:
 * that package cannot be imported here (it reaches `@sb/routine-runner`, which
 * imports `@sb/ai` back), and the three fields below are all this needs.
 */
export function parseDemonstrationSkill(skill: {
  id: string;
  skillType: string;
  shared: unknown;
}): DemonstrationSkillConfig {
  if (skill.skillType !== 'ai-motion') {
    throw new Error(
      `parseDemonstrationSkill: skill ${skill.id} has skillType=${skill.skillType}, expected 'ai-motion'`,
    );
  }

  return DemonstrationSkillConfig.parse(skill.shared);
}

export const ShowPathSkill = BaseSkillSchema.extend({
  skillType: zod.literal('ai-motion'),
  method: ShowPathSkillMethod,
  shared: SharedShowPathSkillConfig,
  methodConfig: zod.record(zod.string(), zod.unknown()).default({}),
});
export type ShowPathSkill = zod.infer<typeof ShowPathSkill>;

// Inspect skill — 2D defect detection against the customer's Roboflow workflow

export const InspectSkillMethod = zod.enum(['roboflow']);
export type InspectSkillMethod = zod.infer<typeof InspectSkillMethod>;

/**
 * One defect class the operator can threshold.
 *
 * `id` is the sanitized `label`, because it becomes a routine-variable key
 * (`count_<id>`) and Roboflow class names hold spaces, hyphens and mixed
 * case that a key cannot — `Reject - Surface Scratch` sanitizes to
 * `Reject_Surface_Scratch`. `label` keeps the raw Roboflow name, which the
 * operator cannot edit.
 */
export const InspectClass = zod.object({
  // `skillsHooks` interpolates the issue's message into a client-visible
  // BadRequest, and zod's default for a failed regex is the bare word
  // "Invalid", which does not say what the constraint is.
  id: zod
    .string()
    .regex(
      /^\w+$/,
      'must contain only letters, digits and underscores (it becomes a routine-variable name)',
    ),
  // `.min(1)` for the same reason as the shared config's strings below: a bare
  // `zod.string()` accepts '', and an unnamed class cannot be matched to a
  // detection or shown on a row.
  label: zod.string().min(1),
  colorToken: zod.string(),
  /**
   * Unset means the operator has not tuned this class, and it is filtered at a
   * low default floor instead.
   *
   * Deliberately NOT the lowest threshold some other class happens to set.
   * Inheriting a sibling's value would mean raising one class to 90% silently
   * tightened every class left alone — and because the lowest effective
   * threshold is also what gets sent to the workflow, those detections would be
   * dropped by the graph before any control here could recover them. In a defect
   * count that reads as the model missing defects rather than as a setting
   * somebody made.
   */
  confidenceThreshold: zod.number().min(0).max(1).optional(),
});
export type InspectClass = zod.infer<typeof InspectClass>;

/**
 * The skill's defect classes: at least one, with distinct ids AND distinct
 * labels.
 *
 * `.min(1)` is the zero-class guard — with none there is nothing to count and
 * nothing to threshold, so a saved skill would report a verdict derived from an
 * empty set with no error raised.
 *
 * Ids must be unique because each one names a routine variable. Enforced here
 * rather than only where the ids are built: the wizard derives them with a
 * sanitizer that reports collisions, but every other writer — REST, the SDK,
 * agent-bot — reaches this schema directly. Without it, two classes sharing
 * `J_clip` parse clean and `count_J_clip` ends up with two owning classes and
 * two thresholds, where whichever the reader reaches first governs the count and
 * the operator's setting silently applies to the wrong defect.
 *
 * Labels must be unique for a sharper reason: the id names the variable, but the
 * LABEL is what every reader matches a detection on, and the readers disagree
 * about which duplicate wins. Two of them resolve with `.find`, so the first
 * entry governs; the counts hook builds a `Map`, so the last one does. Two
 * classes both labelled `Gap` with thresholds 0.9 and 0.1 therefore filter a
 * 0.5 detection out of the overlay while counting it under the other class, and
 * the first class's count reads 0 for the life of the skill. Unlike the id case
 * there is no sanitizer upstream that would catch it, because the labels are
 * whatever Roboflow reported.
 *
 * Refined on the ARRAY rather than on `SharedInspectSkillConfig` itself.
 * Refining the object returns a `ZodEffects`, and `toDraftInput` calls
 * `.partial()` on `variant.shape.shared` behind a cast to `ZodObject` — so an
 * object-level refinement compiles and then throws at module load. Refining the
 * field keeps the object a `ZodObject`, and `.partial()` still drops the whole
 * field for drafts while validating it wherever it is present.
 */
const InspectClassList = zod
  .array(InspectClass)
  .min(1)
  .superRefine((classes, ctx) => {
    const labelById = new Map<string, string>();
    const idByLabel = new Map<string, string>();

    classes.forEach(({ id, label }, index) => {
      // Both checks run for every entry rather than returning early on the
      // first: a payload can collide on one and not the other, and reporting
      // only the id would leave the operator fixing half of it.
      const idOwner = labelById.get(id);

      if (idOwner === undefined) {
        labelById.set(id, label);
      } else {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          path: [index, 'id'],
          message: `duplicate class id "${id}" — already used by "${idOwner}", and each id becomes one routine variable`,
        });
      }

      const labelOwner = idByLabel.get(label);

      if (labelOwner === undefined) {
        idByLabel.set(label, id);
      } else {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          path: [index, 'label'],
          message: `duplicate class label "${label}" — already used by id "${labelOwner}", and every detection is matched to a class by its label`,
        });
      }
    });
  });

export const SharedInspectSkillConfig = zod.object({
  // The viewpoint the skill was trained from, in the same two forms Locate
  // stores it: a freshly-captured pose inline, or a link to a global Space
  // position. Exactly one is set once the Training pose row is complete.
  //
  // Defaulted to null on both so a skill saved before this existed still
  // parses. It reads as "no training pose recorded", which is what such a skill
  // has, and the routine step reports it the way a skill that never finished
  // the row is reported.
  capturePose: PoseSchema.nullable().default(null),
  capturePoseSpaceItemId: zod.string().nullable().default(null),
  // `.min(1)` on all four, and on the runner below: a bare `zod.string()`
  // accepts '', so key presence alone would let a finalized, strictly validated
  // row exist with no camera or workflow and nothing able to execute it — the
  // run path returns null for that state, silently. The wizard's Create button
  // gates the same fields.
  cameraId: zod.string().min(1),
  roboflowWorkspaceName: zod.string().min(1),
  roboflowWorkflowId: zod.string().min(1),
  // One workflow can publish several detection outputs, so the workflow id
  // alone leaves the selection incomplete.
  roboflowOutputName: zod.string().min(1),
  /**
   * Values bound to the workflow's own inputs at run time, to stop it filtering
   * internally (`confidence: '0'`, `iou_threshold: '1'`) so this skill's
   * per-class thresholds are the only thing that filters.
   *
   * Only the workflow's schema says which inputs it accepts, so these are
   * resolved when it is imported. Empty means the graph filters at its own
   * thresholds first, at the floor `roboflowConfidenceFloor` records.
   */
  roboflowWorkflowInputs: zod.record(zod.string(), zod.string()).optional(),
  /**
   * The confidence the workflow's own detection step filters at, read from its
   * cached definition when the skill was imported.
   *
   * Stored because the graph applies it before a single detection reaches us, so
   * it is the real floor for any class the operator has not tuned — and a floor
   * we chose independently would be a number the panel showed and the robot
   * ignored. Persisted rather than re-read per session so a reopened skill
   * reports the value it was built against.
   *
   * Absent where the definition declares nothing readable: a threshold bound to
   * a runtime workflow input, or a `confidence_mode` other than `custom`, where
   * the model filters at a threshold chosen inside it and never published.
   */
  roboflowConfidenceFloor: zod.number().min(0).max(1).optional(),
  /**
   * Whether the imported workflow holds a Dataset Upload or Vision Events block.
   *
   * Cached like `roboflowConfidenceFloor`: the preload is the only moment it is
   * knowable, so a reopened skill reads it back rather than paying another
   * preload to redraw one row.
   *
   * Optional because absence is a third state, where the robot could not say,
   * and `false` means the import looked and found neither. The two get
   * different copy.
   */
  roboflowDeclaresUpload: zod.boolean().optional(),
  /**
   * Which vision-bot preloads and runs the workflow, or `''` for the robot's
   * own.
   *
   * A GPU task runner IS a vision-bot — a remote one — so an empty id is a real
   * choice rather than an unfinished skill: the robot serves the same Roboflow
   * RPCs, more slowly. Defaulted like Locate's for that reason.
   *
   * Whichever it resolves to has to stay the same between preload and run. A
   * workflow warmed on one machine and run on another pays the download again
   * mid-routine, which is why neither path falls back to the other when a
   * configured runner is down.
   */
  gpuTaskRunnerId: zod.string().default(''),
  classes: InspectClassList,
  regionOfInterest: RegionOfInterest.optional(),
  /**
   * A JPEG of the inspected frame with the detection boxes drawn on, captured
   * when the workflow selection is committed. It is the skill's card image and
   * nothing else reads it — the runtime resolves the workflow from the ids
   * above.
   *
   * Absent on a skill saved before this existed, or one whose preview had no
   * frame to capture (the camera stream had not come up yet). The card then
   * shows its placeholder, the same as Locate's.
   */
  snapshotStorageID: zod.string().nullable().optional(),
  /** How many detections that snapshot shows; see Locate's field. */
  snapshotMatchCount: zod.number().int().nonnegative().optional(),
});

export type SharedInspectSkillConfig = zod.infer<
  typeof SharedInspectSkillConfig
>;

export const InspectSkill = BaseSkillSchema.extend({
  skillType: zod.literal('inspect'),
  method: InspectSkillMethod,
  shared: SharedInspectSkillConfig,
  methodConfig: zod.record(zod.string(), zod.unknown()).default({}),
});
export type InspectSkill = zod.infer<typeof InspectSkill>;

// Discriminated union

export const Skill = zod.discriminatedUnion('skillType', [
  ControlSkill,
  LocateSkill,
  ClassifySkill,
  AgentSkill,
  ShowPathSkill,
  InspectSkill,
]);
export type Skill = zod.infer<typeof Skill>;

const toSkillInput = <
  T extends
    | typeof ControlSkill
    | typeof LocateSkill
    | typeof ClassifySkill
    | typeof AgentSkill
    | typeof ShowPathSkill
    | typeof InspectSkill,
>(
  variant: T,
) =>
  variant
    .omit({ id: true, createdAt: true, updatedAt: true, description: true })
    .extend({ description: zod.string().nullable().optional() });

export const SkillInput = zod.discriminatedUnion('skillType', [
  toSkillInput(ControlSkill),
  toSkillInput(LocateSkill),
  toSkillInput(ClassifySkill),
  toSkillInput(AgentSkill),
  toSkillInput(ShowPathSkill),
  toSkillInput(InspectSkill),
]);
export type SkillInput = zod.infer<typeof SkillInput>;

/**
 * The plain object a variant's `shared` is built from. Show-path's is wrapped
 * in a `ZodEffects` by its trim-window refinement, which has no `.partial()`;
 * unwrapping keeps the draft schema derived rather than hand-written per
 * variant; `toDraftInput` re-applies what the unwrap dropped.
 */
const sharedObjectOf = (
  shared: zod.ZodTypeAny,
): zod.ZodObject<zod.ZodRawShape> =>
  shared instanceof zod.ZodEffects
    ? sharedObjectOf(shared.innerType() as zod.ZodTypeAny)
    : (shared as zod.ZodObject<zod.ZodRawShape>);

const toDraftInput = <
  T extends
    | typeof ControlSkill
    | typeof LocateSkill
    | typeof ClassifySkill
    | typeof AgentSkill
    | typeof ShowPathSkill
    | typeof InspectSkill,
>(
  variant: T,
) =>
  toSkillInput(variant).extend({
    // Draft leniency is about which fields are PRESENT, not about accepting a
    // combination the strict schema rejects — so the cross-field rules the
    // unwrap dropped go back on. `checkTrimWindowOrdering` early-returns
    // unless both bounds are set, so it is inert on the other variants.
    shared: sharedObjectOf(variant.shape.shared)
      .partial()
      .superRefine(checkTrimWindowOrdering),
  });

/**
 * Lenient validation for wizard DRAFT rows (`skills.isDraft`). Derived
 * mechanically from the strict variants: every `shared` key becomes
 * optional, so a mid-wizard row may omit fields its step hasn't produced
 * yet (a locate draft has no `cameraId` before the camera step) — but any
 * field that IS present still gets full shape checking. The draft→skill
 * transition re-validates against the strict `SkillInput`.
 */
export const DraftSkillInput = zod.discriminatedUnion('skillType', [
  toDraftInput(ControlSkill),
  toDraftInput(LocateSkill),
  toDraftInput(ClassifySkill),
  toDraftInput(AgentSkill),
  toDraftInput(ShowPathSkill),
  toDraftInput(InspectSkill),
]);
export type DraftSkillInput = zod.infer<typeof DraftSkillInput>;
